import { Link } from "react-router-dom";

const PILLARS = [
  { n: "I", name: "Lean", tag: "Minimal. Essential." },
  { n: "II", name: "Six Sigma", tag: "Zero defects." },
  { n: "III", name: "Design Thinking", tag: "Empathy first." },
  { n: "IV", name: "Systems Thinking", tag: "Flows & loops." },
  { n: "V", name: "Chaos Engineering", tag: "Break it first." },
  { n: "VI", name: "Agile / Scrum", tag: "Iterate by sprint." },
];

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section
        style={{
          position: "relative",
          minHeight: "92vh",
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: "1fr",
          alignItems: "center",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "url('https://images.unsplash.com/photo-1768741876647-0c39850cdc33?crop=entropy&cs=srgb&fm=jpg&w=2000&q=70')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.3) brightness(0.65)",
          }}
        />
        <div className="hero-overlay" />

        <div
          style={{
            position: "relative",
            zIndex: 2,
            padding: "80px 6vw",
            display: "grid",
            gridTemplateColumns: "1.3fr 1fr",
            gap: 60,
            alignItems: "center",
          }}
        >
          <div className="reveal reveal-d1">
            <div className="overline" style={{ marginBottom: 24 }}>
              ✦ Codex · MMXXVI · The Lyceum of Applied Systems
            </div>
            <h1
              className="font-serif"
              style={{
                fontSize: "clamp(48px, 7vw, 96px)",
                lineHeight: 0.95,
                color: "var(--parchment)",
                letterSpacing: "-0.02em",
                marginBottom: 28,
              }}
            >
              Build applications <br />
              <span className="font-display" style={{ color: "var(--brass)" }}>the way Aristotle</span> <br />
              would have.
            </h1>
            <p
              className="font-serif"
              style={{
                fontSize: 20,
                lineHeight: 1.55,
                maxWidth: 560,
                color: "var(--parchment-dim)",
                fontStyle: "italic",
                marginBottom: 44,
              }}
            >
              HEXIS OS is a meta-architect for students and founders. Every application you conjure here
              must pass six disciplines — <em>Lean, Six Sigma, Design Thinking, Systems Thinking,
              Chaos Engineering, Agile</em> — before it earns the right to deploy.
            </p>
            <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
              <Link to="/auth" data-testid="landing-enter-cta" className="btn-brass" style={{ padding: "18px 36px", fontSize: 13 }}>
                Enter the Lyceum →
              </Link>
              <a href="#disciplines" data-testid="landing-explore-cta" className="btn-ghost">
                The Six Disciplines
              </a>
            </div>
          </div>

          <div className="reveal reveal-d3" style={{ position: "relative" }}>
            <div
              className="glass"
              style={{
                padding: 22,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                color: "var(--terminal)",
                minHeight: 300,
              }}
            >
              <div style={{ color: "var(--brass)", marginBottom: 10, fontSize: 10, letterSpacing: "0.25em" }}>
                / dev / hexis / terminal
              </div>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.75 }}>
{`> hexis init sprint --student "aristotle"
✓ Lyceum session opened
✓ Model: claude-sonnet-4.5 | deepseek-reasoner
✓ 6/6 disciplines loaded

> hexis audit --discipline "chaos"
↳ injecting network partition...
↳ graceful degrade ..................... PASS
↳ db retry contract .................... PASS
↳ edge-case fuzz ........................ WARN

> hexis deploy --sprint "athena-mvp"
↳ zipping 6 files ........... 3.4 KB
↳ hexis_manifest.json ....... signed
✦ artifact exported.`}
              </pre>
            </div>
            <div className="font-display" style={{ marginTop: 14, color: "var(--muted)", fontSize: 12, textAlign: "right" }}>
              Fig. 1 — a single Lyceum session.
            </div>
          </div>
        </div>
      </section>

      {/* DISCIPLINES */}
      <section id="disciplines" style={{ padding: "120px 6vw", position: "relative" }}>
        <div className="divider-label" style={{ marginBottom: 40 }}>§ II — The Six Disciplines of HEXIS</div>
        <h2
          className="font-serif"
          style={{ fontSize: "clamp(36px, 5vw, 60px)", maxWidth: 900, lineHeight: 1.05, marginBottom: 64 }}
        >
          Six instruments. One <span className="font-display" style={{ color: "var(--brass)" }}>indivisible</span> method.
        </h2>

        <div
          className="pillars-grid"
          data-testid="pillars-grid"
        >
          {PILLARS.map((p, i) => (
            <div
              key={p.name}
              className="reveal"
              style={{
                background: "var(--ink-surface)",
                padding: 36,
                animationDelay: `${0.08 * i}s`,
              }}
              data-testid={`pillar-${p.name.toLowerCase().replace(/ /g, "-")}`}
            >
              <div className="font-display" style={{ color: "var(--brass)", fontSize: 28, marginBottom: 20 }}>
                {p.n}.
              </div>
              <div className="font-serif" style={{ fontSize: 28, color: "var(--parchment)", marginBottom: 8 }}>
                {p.name}
              </div>
              <div className="font-mono" style={{ fontSize: 11, letterSpacing: "0.18em", color: "var(--parchment-dim)", textTransform: "uppercase" }}>
                {p.tag}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section style={{ padding: "100px 6vw" }}>
        <div className="divider-label" style={{ marginBottom: 40 }}>§ III — The Rite of Passage</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 60, alignItems: "start" }}>
          <div>
            <h3 className="font-serif" style={{ fontSize: 44, lineHeight: 1.05, marginBottom: 24 }}>
              From whisper to <br />
              <span className="font-display" style={{ color: "var(--brass)" }}>artifact.</span>
            </h3>
            <p className="font-serif" style={{ fontSize: 18, color: "var(--parchment-dim)", fontStyle: "italic", lineHeight: 1.6 }}>
              Every sprint walks five numbered phases. The Deploy button remains locked
              until the Chaos Engineering Test is survived.
            </p>
          </div>
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {[
              ["Discovery", "Converse with the Mentor. Describe the app you intend to build."],
              ["HEXIS Audit", "Pass the six-discipline scrutiny. Each pillar issues a verdict."],
              ["Generation", "Claude for speed; DeepSeek for deep logic. Live code preview."],
              ["Chaos Test", "Fail injection — network, database, edge-case input — must survive."],
              ["Deploy", "Receive the sealed ZIP. Your artifact is granted passage."],
            ].map(([t, d], i) => (
              <li
                key={t}
                style={{
                  display: "grid",
                  gridTemplateColumns: "72px 1fr",
                  gap: 24,
                  padding: "22px 0",
                  borderTop: i === 0 ? "none" : "1px solid rgba(197,160,89,0.18)",
                }}
              >
                <span className="num-stamp">{i + 1}</span>
                <div>
                  <div className="font-serif" style={{ fontSize: 26, color: "var(--parchment)" }}>{t}</div>
                  <div className="font-mono" style={{ fontSize: 12.5, color: "var(--parchment-dim)", marginTop: 4, lineHeight: 1.6 }}>{d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        style={{
          borderTop: "1px solid rgba(197,160,89,0.2)",
          padding: "40px 6vw",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div className="font-display" style={{ color: "var(--muted)", fontSize: 14, fontStyle: "italic" }}>
          "We are what we repeatedly do. Excellence, then, is not an act, but a habit." — Ἀριστοτέλης
        </div>
        <div className="overline">HEXIS · MMXXVI</div>
      </footer>
    </div>
  );
}
